import { useState } from 'react';
import { PDFDocument, StandardFonts, degrees, rgb } from 'pdf-lib';
import QRCode from 'qrcode';
import qrcard from '../assets/img/starthere/qrcard.pdf';
import spcWelcomeCard from 'assets/img/starthere/spc_welcome_card.pdf';

interface QRPaymentData {
  redirectUrl?: string;
  [key: string]: any;
}

interface QRCardConfig {
  basePdf?: 'qrcard' | 'spcWelcomeCard'; // selecciona el diseño
  position?: { x: number; y: number; width: number; height: number };
  filename?: string;
  sessionName?: string;
}

export function useQRCodePDF() {
  const [isGenerating, setIsGenerating] = useState(false);

  async function generateQRCodePDF(
    paymentData: QRPaymentData | string,
    config: QRCardConfig = {}
  ): Promise<Uint8Array> {
    const qrUrl =
      typeof paymentData === 'string'
        ? paymentData
        : paymentData?.redirectUrl;

    if (!qrUrl) throw new Error('No redirectUrl in backend response');

    const { basePdf = 'qrcard', position } = config;

    const qrDataUrl = await QRCode.toDataURL(qrUrl, { width: 300, margin: 1 });

    // Selecciona el PDF base según el diseño
    const pdfSource = basePdf === 'spcWelcomeCard' ? spcWelcomeCard : qrcard;

    const response = await fetch(pdfSource);
    if (!response.ok) throw new Error('Unable to load base PDF');
    const pdfBytes = await response.arrayBuffer();

    const pdfDoc = await PDFDocument.load(pdfBytes);
    const pages = pdfDoc.getPages();
    const page = pages[0];
    const qrImage = await pdfDoc.embedPng(qrDataUrl);

    // Usa posición por defecto o la del config
    const { x, y, width, height } =
    position || { x: 64, y: 190, width: 150, height: 150 };

    page.drawImage(qrImage, { x, y, width, height });

    // Draw session name upside-down on the upper fold (only for qrcard)
    if (basePdf === 'qrcard' && config.sessionName) {
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontSize = 10;
      const text = config.sessionName;
      const textWidth = font.widthOfTextAtSize(text, fontSize);
      const pageHeight = page.getHeight();
      // Position: left side, at the height of the WhatsApp phone area (~85% of page height)
      // Text is rotated 180° so it reads correctly when the sheet is folded
      const textX = 20 + textWidth;
      const textY = pageHeight * 0.85;
      page.drawText(text, {
        x: textX,
        y: textY,
        size: fontSize,
        font,
        color: rgb(0.2, 0.2, 0.2),
        rotate: degrees(180),
      });
    }

    return await pdfDoc.save();
  }

  async function downloadQRCodePDF(
    paymentData: QRPaymentData | string,
    config: QRCardConfig = {}
  ) {
    if (!paymentData) {
      console.error('No payment data provided');
      return;
    }

    const { filename = 'Sprintcheckout-card.pdf' } = config;

    setIsGenerating(true);
    try {
      const modifiedPdf = await generateQRCodePDF(paymentData, config);
      const blob = new Blob([modifiedPdf], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Error generando PDF con QR:', err);
      throw err;
    } finally {
      setIsGenerating(false);
    }
  }

  return {
    downloadQRCodePDF,
    generateQRCodePDF,
    isGenerating,
  };
}
